import { EmptyState } from "@/components/molecules/empty-state";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import type { Qualification } from "@/features/qualifications/types";
import { useTranslation } from "@/features/utils/i18n";
import { useTitle } from "@/features/utils/use-title";
import { ClipboardTextIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CreateQualificationDialog } from "./components/create-qualification-dialog";
import {
  SubmissionList,
  useSubmissionList,
} from "./components/submission-list";
import { SubmissionView } from "./components/submission-view";

/**
 * The submissions, as the call history: the list on the left, the selected one
 * in full on the right (`calls/index.tsx` — same frame, same reasons). It used
 * to be a table opening a dialog, and reviewing ten submissions meant opening
 * and closing it ten times; here the next one is a click, or ↓, away — and
 * approving or rejecting moves on to it by itself.
 *
 * The selection is the URL (`/qualifications/:qualificationId`), so a link to a
 * submission (a contact's file, a notification) opens it in place.
 */
export function QualificationsPage() {
  const { t } = useTranslation("qualifications");
  const navigate = useNavigate();
  const { qualificationId } = useParams<{ qualificationId: string }>();
  const clientId = useCurrentClient()?.client?.id || "";
  useTitle(t("submissions.title"));

  const list = useSubmissionList();
  const [createOpen, setCreateOpen] = useState(false);

  const base = `/${clientId}/qualifications`;
  const select = (qualification: Qualification) =>
    navigate(`${base}/${qualification.id}`);
  // Stepping through the list replaces the entry: "back" leaves the queue
  // rather than walking it again backwards.
  const step = (qualification?: Qualification) =>
    qualification
      ? () => navigate(`${base}/${qualification.id}`, { replace: true })
      : undefined;

  const index = qualificationId
    ? list.items.findIndex((item) => item.id === qualificationId)
    : -1;
  const previous = index > 0 ? list.items[index - 1] : undefined;
  const next = index >= 0 ? list.items[index + 1] : undefined;

  // ↑/↓ (and k/j) step through the list from anywhere on the page but a field
  // being typed in or a dialog over it. The handler is read through a ref so the
  // listener is attached once.
  const keys = useRef<(event: KeyboardEvent) => void>(() => {});
  keys.current = (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        "input, textarea, select, [contenteditable=true], [role=dialog], [role=listbox], [role=menu]",
      )
    ) {
      return;
    }
    const goTo =
      event.key === "ArrowDown" || event.key === "j"
        ? (next ?? (index < 0 ? list.items[0] : undefined))
        : event.key === "ArrowUp" || event.key === "k"
          ? previous
          : undefined;
    if (!goTo) return;
    event.preventDefault();
    step(goTo)?.();
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => keys.current(event);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {/* Flush with the application frame, as the call history: see the
          comments in `calls/index.tsx` for the margins and the height. */}
      <div className="-mx-4 flex h-[calc(100dvh-3rem)] overflow-hidden bg-white md:-mx-8 md:h-[calc(100dvh-1rem)] md:rounded-lg dark:bg-slate-950">
        {/* On a phone, one pane at a time: the list, then the submission. */}
        <div
          className={`min-h-0 w-full flex-col border-r md:flex md:w-[22rem] md:shrink-0 lg:w-[24rem] ${qualificationId ? "hidden" : "flex"}`}
        >
          <SubmissionList
            list={list}
            selectedId={qualificationId}
            onSelect={select}
            onCreate={() => setCreateOpen(true)}
          />
        </div>

        <div
          className={`min-h-0 min-w-0 flex-1 flex-col ${qualificationId ? "flex" : "hidden md:flex"}`}
        >
          {qualificationId ? (
            <SubmissionView
              key={qualificationId}
              qualificationId={qualificationId}
              fallback={index >= 0 ? list.items[index] : undefined}
              position={index >= 0 ? { index, total: list.total } : undefined}
              onPrevious={step(previous)}
              onNext={step(next)}
              onBack={() => navigate(base)}
              onReviewed={() => step(next)?.()}
            />
          ) : (
            <EmptyState
              compact
              icon={ClipboardTextIcon}
              title={t("submissions.list.select_prompt")}
              description={t("submissions.list.select_hint")}
              className="h-full"
            />
          )}
        </div>
      </div>

      <CreateQualificationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </>
  );
}
